import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Home,
  MessageSquare,
  TestTube,
  TrendingUp,
  Users,
  Mail,
  Rocket,
  Menu,
  X,
  LogOut,
  Award,
} from 'lucide-react';

// Utility for merging class names
const cn = (...classes: (string | undefined | null | false)[]) => {
  return classes.filter(Boolean).join(' ');
};

interface ChampionsDashboardLayoutProps {
  children: React.ReactNode;
  championName?: string;
  championNumber?: number;
}

const navigationItems = [
  {
    id: 'welcome',
    label: 'Welcome',
    icon: Home,
    path: '/champions/dashboard',
    description: 'Your impact & stats',
  },
  {
    id: 'feedback',
    label: 'Collect Feedback',
    icon: MessageSquare,
    path: '/champions/dashboard/feedback',
    description: 'Gather neighbor input',
  },
  {
    id: 'demos',
    label: 'Feature Demos',
    icon: TestTube,
    path: '/champions/dashboard/demos',
    description: 'Test & share features',
  },
  {
    id: 'voting',
    label: 'Priority Voting',
    icon: TrendingUp,
    path: '/champions/dashboard/voting',
    description: 'Vote on features',
  },
  {
    id: 'directory',
    label: 'Champions Directory',
    icon: Users,
    path: '/champions/dashboard/directory',
    description: 'Fellow Champions',
  },
  {
    id: 'messaging',
    label: 'Messages',
    icon: Mail,
    path: '/champions/dashboard/messages',
    description: 'Contact our team',
  },
  {
    id: 'launch',
    label: 'Launch Prep',
    icon: Rocket,
    path: '/champions/dashboard/launch',
    description: 'Get ready to launch',
  },
];

export function ChampionsDashboardLayout({
  children,
  championName = 'Champion',
  championNumber = 1,
}: ChampionsDashboardLayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();

  const isActivePath = (path: string) => {
    if (path === '/champions/dashboard') {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container flex h-16 items-center justify-between px-4">
          {/* Logo & Champion Badge */}
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setSidebarOpen(!sidebarOpen)}
            >
              {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
            <div className="flex items-center gap-2">
              <Award className="h-6 w-6 text-primary" />
              <span className="font-bold text-lg hidden sm:inline">Champions Dashboard</span>
              <span className="font-bold text-lg sm:hidden">Dashboard</span>
            </div>
          </div>

          {/* Champion Info */}
          <div className="flex items-center gap-3">
            <div className="text-right hidden sm:block">
              <p className="text-sm font-medium">{championName}</p>
              <p className="text-xs text-muted-foreground">Champion #{championNumber}</p>
            </div>
            <Badge variant="outline" className="hidden sm:flex">
              <Award className="h-3 w-3 mr-1" />
              Active
            </Badge>
          </div>
        </div>
      </header>

      <div className="container flex px-4 py-6 gap-6">
        {/* Sidebar Navigation - Desktop */}
        <aside className="hidden md:block w-64 shrink-0">
          <Card className="sticky top-24 p-4">
            <nav className="space-y-1">
              {navigationItems.map((item) => {
                const Icon = item.icon;
                const isActive = isActivePath(item.path);

                return (
                  <Link
                    key={item.id}
                    to={item.path}
                    className={cn(
                      'flex items-start gap-3 rounded-lg px-3 py-3 transition-all hover:bg-accent group',
                      isActive && 'bg-primary text-primary-foreground hover:bg-primary/90',
                    )}
                  >
                    <Icon
                      className={cn(
                        'h-5 w-5 mt-0.5 shrink-0',
                        isActive
                          ? 'text-primary-foreground'
                          : 'text-muted-foreground group-hover:text-white',
                      )}
                    />
                    <div className="flex-1 min-w-0">
                      <p
                        className={cn(
                          'text-sm font-medium',
                          isActive
                            ? 'text-primary-foreground'
                            : 'text-foreground group-hover:text-white',
                        )}
                      >
                        {item.label}
                      </p>
                      <p
                        className={cn(
                          'text-xs truncate',
                          isActive
                            ? 'text-primary-foreground/80'
                            : 'text-muted-foreground group-hover:text-white',
                        )}
                      >
                        {item.description}
                      </p>
                    </div>
                  </Link>
                );
              })}
            </nav>

            <div className="mt-6 pt-6 border-t">
              <Button
                variant="ghost"
                className="w-full justify-start text-muted-foreground"
                size="sm"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Sign Out
              </Button>
            </div>
          </Card>
        </aside>

        {/* Mobile Sidebar Overlay */}
        {sidebarOpen && (
          <>
            <div
              className="fixed inset-0 z-40 bg-black/50 md:hidden"
              onClick={() => setSidebarOpen(false)}
            />
            <aside className="fixed left-0 top-16 bottom-0 z-50 w-72 bg-background border-r md:hidden overflow-y-auto">
              <Card className="m-4 p-4 border-0 shadow-none">
                <nav className="space-y-1">
                  {navigationItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = isActivePath(item.path);

                    return (
                      <Link
                        key={item.id}
                        to={item.path}
                        onClick={() => setSidebarOpen(false)}
                        className={cn(
                          'flex items-start gap-3 rounded-lg px-3 py-3 transition-all hover:bg-accent group',
                          isActive && 'bg-primary text-primary-foreground hover:bg-primary/90',
                        )}
                      >
                        <Icon
                          className={cn(
                            'h-5 w-5 mt-0.5 shrink-0',
                            isActive
                              ? 'text-primary-foreground'
                              : 'text-muted-foreground group-hover:text-accent-foreground',
                          )}
                        />
                        <div className="flex-1 min-w-0">
                          <p
                            className={cn(
                              'text-sm font-medium',
                              isActive
                                ? 'text-primary-foreground'
                                : 'text-foreground group-hover:text-accent-foreground',
                            )}
                          >
                            {item.label}
                          </p>
                          <p
                            className={cn(
                              'text-xs truncate',
                              isActive
                                ? 'text-primary-foreground/80'
                                : 'text-muted-foreground group-hover:text-accent-foreground/80',
                            )}
                          >
                            {item.description}
                          </p>
                        </div>
                      </Link>
                    );
                  })}
                </nav>

                <div className="mt-6 pt-6 border-t">
                  <Button
                    variant="ghost"
                    className="w-full justify-start text-muted-foreground"
                    size="sm"
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    Sign Out
                  </Button>
                </div>
              </Card>
            </aside>
          </>
        )}

        {/* Main Content */}
        <main className="flex-1 min-w-0">{children}</main>
      </div>
    </div>
  );
}
